class ArticleProductGridCTAController {
  constructor(root) {
    this.root = root;
    this.cards = [];
    this.rootPath = window.Shopify?.routes?.root || '/';
  }

  async initialize() {
    this.cards = Array.from(
      this.root.querySelectorAll('.js-article-cta-card')
    ).map((element) => this.createCard(element));

    await Promise.all(
      this.cards.map((card) => this.loadCard(card))
    );
  }

  createCard(element) {
    return {
      element,
      handle: element.dataset.productHandle,
      productId: Number(element.dataset.productId || 0),
      currentColor: element.dataset.currentColor || '',
      product: null,
      colors: {},
      bandIndex: -1,
      cupIndex: -1,
      sizeIndex: -1,
      colorIndex: -1,
      selectedOptions: {},
      selectedVariant: null,
      image: element.querySelector('.js-article-cta-image'),
      title: element.querySelector('.js-article-cta-title'),
      productLinks: element.querySelectorAll(
        '.js-article-cta-product-link'
      ),
      price: element.querySelector('.js-article-cta-price'),
      comparePrice: element.querySelector(
        '.js-article-cta-compare-price'
      ),
      swatches: element.querySelector(
        '.js-article-cta-swatches'
      ),
      sizeToggle: element.querySelector(
        '.js-article-cta-size-toggle'
      ),
      sizePanel: element.querySelector(
        '.js-article-cta-size-panel'
      ),
      sizeHeading: element.querySelector(
        '.js-article-cta-size-heading'
      ),
      bandGroup: element.querySelector(
        '.js-article-cta-band-group'
      ),
      bandOptions: element.querySelector(
        '.js-article-cta-band-options'
      ),
      cupGroup: element.querySelector(
        '.js-article-cta-cup-group'
      ),
      cupOptions: element.querySelector(
        '.js-article-cta-cup-options'
      ),
      sizeGroup: element.querySelector(
        '.js-article-cta-size-group'
      ),
      sizeOptions: element.querySelector(
        '.js-article-cta-size-options'
      ),
      addButton: element.querySelector(
        '.js-article-cta-add'
      ),
      status: element.querySelector(
        '.js-article-cta-status'
      ),
    };
  }

  async loadCard(card) {
    this.setStatus(card, 'Loading options...');

    try {
      const [product, colorResponse] = await Promise.all([
        this.fetchProduct(card.handle),
        this.fetchColors(card.handle),
      ]);

      card.product = product;
      card.productId = Number(product.id);
      card.colors = colorResponse.colors || {};

      this.configureOptionIndexes(card);
      this.renderColorSwatches(card);
      this.renderSizeOptions(card);
      this.bindCardEvents(card);
      this.updateCard(card);
      this.setStatus(card, '');
    } catch (error) {
      console.error('Article product CTA:', error);

      this.setStatus(
        card,
        'Product options could not be loaded.',
        true
      );

      if (card.sizeToggle) {
        card.sizeToggle.disabled = true;
      }
    }
  }

  async fetchProduct(handle) {
    const response = await fetch(
      `${this.rootPath}products/${encodeURIComponent(handle)}.js`,
      {
        headers: {
          Accept: 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Unable to load product: ${handle}`);
    }

    const product = await response.json();

    if (
      !Array.isArray(product.variants) ||
      !product.variants.length
    ) {
      throw new Error(`No variants found for: ${handle}`);
    }

    return product;
  }

  async fetchColors(handle) {
    try {
      const url =
        `/products/${encodeURIComponent(handle)}` +
        '?view=article-product-json';

      const response = await fetch(url, {
        credentials: 'same-origin',
        cache: 'no-store',
      });

      if (!response.ok) {
        throw new Error(
          `Color request failed with status ${response.status}`
        );
      }

      const text = (await response.text())
        .replace(/^\uFEFF/, '')
        .trim();

      const jsonStart = text.indexOf('{');
      const jsonEnd = text.lastIndexOf('}');

      if (jsonStart < 0 || jsonEnd < jsonStart) {
        throw new Error(
          'The color endpoint did not return valid JSON.'
        );
      }

      const data = JSON.parse(
        text.slice(jsonStart, jsonEnd + 1)
      );

      if (
        !data ||
        typeof data.colors !== 'object' ||
        Array.isArray(data.colors)
      ) {
        throw new Error(
          'The color endpoint did not include a colors object.'
        );
      }

      return data;
    } catch (error) {
      console.warn('Article product CTA colors:', error);

      return {
        colors: {},
      };
    }
  }

  bindCardEvents(card) {
    if (card.sizeToggle) {
      card.sizeToggle.addEventListener('click', () => {
        const willOpen = card.sizePanel.hidden;

        this.closeOtherPanels(card);

        card.sizePanel.hidden = !willOpen;

        card.sizeToggle.setAttribute(
          'aria-expanded',
          willOpen ? 'true' : 'false'
        );
      });
    }

    if (card.addButton) {
      card.addButton.addEventListener('click', () => {
        this.addToBag(card);
      });
    }
  }

  closeOtherPanels(activeCard) {
    this.cards.forEach((card) => {
      if (
        card === activeCard ||
        !card.sizePanel
      ) {
        return;
      }

      card.sizePanel.hidden = true;

      if (card.sizeToggle) {
        card.sizeToggle.setAttribute(
          'aria-expanded',
          'false'
        );
      }
    });
  }

  configureOptionIndexes(card) {
    const optionNames = this.optionNames(card.product);

    card.bandIndex = optionNames.findIndex((name) =>
      /band/i.test(name)
    );

    card.cupIndex = optionNames.findIndex((name) =>
      /cup/i.test(name)
    );

    card.sizeIndex = optionNames.findIndex((name) =>
      /size/i.test(name)
    );

    card.colorIndex = optionNames.findIndex((name) =>
      /(color|colour|shade)/i.test(name)
    );

    if (
      card.bandIndex < 0 &&
      card.cupIndex < 0 &&
      optionNames.length >= 2
    ) {
      const firstValues = this.optionValues(
        card.product,
        0
      );

      const secondValues = this.optionValues(
        card.product,
        1
      );

      const looksLikeBands = firstValues.some((value) =>
        /^\d{2,3}$/.test(value)
      );

      const looksLikeCups = secondValues.some((value) =>
        /^[a-z]+[½]?$/i.test(value)
      );

      if (looksLikeBands && looksLikeCups) {
        card.bandIndex = 0;
        card.cupIndex = 1;
      }
    }

    if (
      card.bandIndex < 0 &&
      card.cupIndex < 0 &&
      card.sizeIndex < 0
    ) {
      const usableIndex = optionNames.findIndex(
        (name) =>
          !/(color|colour|shade)/i.test(name)
      );

      card.sizeIndex =
        usableIndex >= 0 ? usableIndex : 0;
    }
  }

  optionNames(product) {
    if (!Array.isArray(product.options)) {
      return [];
    }

    return product.options.map((option) =>
      String(
        typeof option === 'string'
          ? option
          : option.name || ''
      ).trim()
    );
  }

  optionValues(product, index) {
    const values = new Set();

    product.variants.forEach((variant) => {
      const value = this.getOption(
        variant,
        index
      );

      if (value) {
        values.add(value);
      }
    });

    return Array.from(values);
  }

  renderColorSwatches(card) {
    if (!card.swatches) {
      return;
    }

    card.swatches.innerHTML = '';

    let colors = Object.entries(
      card.colors || {}
    );

    if (!colors.length) {
      const fallbackColor =
        card.currentColor || 'Current color';

      colors = [
        [
          fallbackColor,
          {
            id: card.productId,
            url:
              card.product.url ||
              `${this.rootPath}products/${card.handle}`,
            images: card.product.featured_image
              ? [
                  {
                    src: card.product.featured_image,
                  },
                ]
              : [],
          },
        ],
      ];
    }

    const currentEntry = colors.find(
      ([, colorData]) =>
        Number(colorData.id) === card.productId
    );

    if (currentEntry) {
      card.currentColor = currentEntry[0];
    }

    colors.forEach(([colorName, colorData]) => {
      const button =
        document.createElement('button');

      const selected =
        Number(colorData.id) === card.productId;

      button.type = 'button';

      button.className =
        'article-product-grid-cta__swatch ' +
        'js-article-cta-swatch';

      button.dataset.color = colorName;

      button.dataset.productId = String(
        colorData.id || ''
      );

      button.dataset.productUrl =
        colorData.url || '';

      button.title =
        this.formatColorName(colorName);

      button.setAttribute(
        'aria-label',
        `Select color ${this.formatColorName(colorName)}`
      );

      button.setAttribute(
        'aria-pressed',
        selected ? 'true' : 'false'
      );

      button.classList.toggle(
        'is-selected',
        selected
      );

      button.style.setProperty(
        '--cta-swatch-color',
        this.swatchColor(colorName)
      );

      button.addEventListener('click', () => {
        this.changeColor(
          card,
          colorName,
          colorData,
          button
        );
      });

      card.swatches.appendChild(button);
    });
  }

  async changeColor(
    card,
    colorName,
    colorData,
    button
  ) {
    if (button.classList.contains('is-selected')) {
      return;
    }

    const handle = this.handleFromUrl(
      colorData.url
    );

    if (!handle) {
      return;
    }

    this.setStatus(card, 'Loading color...');
    card.element.classList.add('is-loading');

    try {
      const product =
        await this.fetchProduct(handle);

      card.handle = handle;
      card.product = product;
      card.productId = Number(product.id);
      card.currentColor = colorName;
      card.selectedOptions = {};
      card.selectedVariant = null;

      this.configureOptionIndexes(card);

      this.updateProductLinks(
        card,
        colorData.url || product.url
      );

      if (card.title && product.title) {
        card.title.textContent = product.title;
      }

      const colorImage =
        colorData.images &&
        colorData.images[0] &&
        colorData.images[0].src;

      if (colorImage && card.image) {
        card.image.src = colorImage;
        card.image.srcset = '';
      } else if (
        product.featured_image &&
        card.image
      ) {
        card.image.src =
          product.featured_image;

        card.image.srcset = '';
      }

      card.swatches
        .querySelectorAll(
          '.js-article-cta-swatch'
        )
        .forEach((swatch) => {
          const active = swatch === button;

          swatch.classList.toggle(
            'is-selected',
            active
          );

          swatch.setAttribute(
            'aria-pressed',
            active ? 'true' : 'false'
          );
        });

      this.renderSizeOptions(card);
      this.updateCard(card);
      this.setStatus(card, '');
    } catch (error) {
      console.error(
        'Article product CTA color change:',
        error
      );

      this.setStatus(
        card,
        'That color could not be loaded.',
        true
      );
    } finally {
      card.element.classList.remove(
        'is-loading'
      );
    }
  }

  handleFromUrl(url) {
    if (!url) {
      return '';
    }

    try {
      const parsed = new URL(
        url,
        window.location.origin
      );

      const parts = parsed.pathname
        .split('/')
        .filter(Boolean);

      const productsIndex =
        parts.indexOf('products');

      return productsIndex >= 0
        ? parts[productsIndex + 1] || ''
        : '';
    } catch (error) {
      return '';
    }
  }

  updateProductLinks(card, url) {
    if (!url) {
      return;
    }

    card.productLinks.forEach((link) => {
      const isSizeGuide =
        link.classList.contains(
          'article-product-grid-cta__size-guide'
        );

      link.href = isSizeGuide
        ? `${url}#size-guide`
        : url;
    });
  }

  renderSizeOptions(card) {
    card.selectedOptions = {};
    card.selectedVariant = null;

    if (card.bandOptions) {
      card.bandOptions.innerHTML = '';
    }

    if (card.cupOptions) {
      card.cupOptions.innerHTML = '';
    }

    if (card.sizeOptions) {
      card.sizeOptions.innerHTML = '';
    }

    const usesBandAndCup =
      card.bandIndex >= 0 &&
      card.cupIndex >= 0;

    if (card.bandGroup) {
      card.bandGroup.hidden =
        !usesBandAndCup;
    }

    if (card.cupGroup) {
      card.cupGroup.hidden =
        !usesBandAndCup;
    }

    if (card.sizeGroup) {
      card.sizeGroup.hidden =
        usesBandAndCup;
    }

    if (usesBandAndCup) {
      this.createOptionButtons(
        card,
        card.bandIndex,
        card.bandOptions
      );

      this.createOptionButtons(
        card,
        card.cupIndex,
        card.cupOptions
      );
    } else {
      this.createOptionButtons(
        card,
        card.sizeIndex,
        card.sizeOptions
      );
    }

    this.updateOptionAvailability(card);
  }

  createOptionButtons(
    card,
    optionIndex,
    container
  ) {
    if (optionIndex < 0 || !container) {
      return;
    }

    this.optionValues(
      card.product,
      optionIndex
    )
      .sort((a, b) =>
        a.localeCompare(
          b,
          undefined,
          {
            numeric: true,
            sensitivity: 'base',
          }
        )
      )
      .forEach((value) => {
        const button =
          document.createElement('button');

        button.type = 'button';

        button.className =
          'article-product-grid-cta__option ' +
          'js-article-cta-option';

        button.dataset.optionIndex =
          String(optionIndex);

        button.dataset.optionValue =
          value;

        button.textContent = value;

        button.setAttribute(
          'aria-pressed',
          'false'
        );

        button.addEventListener(
          'click',
          () => {
            if (button.disabled) {
              return;
            }

            card.selectedOptions[
              optionIndex
            ] = value;

            this.updateCard(card);
          }
        );

        container.appendChild(button);
      });
  }

  updateCard(card) {
    this.updateSelectedButtons(card);
    this.updateOptionAvailability(card);

    const selectionComplete =
      this.isSelectionComplete(card);

    const variant = selectionComplete
      ? this.findSelectedVariant(card)
      : null;

    card.selectedVariant = variant;

    if (selectionComplete && variant) {
      const sizeLabel =
        this.selectedSizeLabel(card);

      card.sizeToggle.textContent =
        `Size ${sizeLabel}`;

      card.sizeHeading.textContent =
        `Size ${sizeLabel}`;

      card.addButton.textContent =
        variant.available === false
          ? 'Sold Out'
          : 'Add to Bag';

      card.addButton.disabled =
        variant.available === false;

      this.updatePrice(card, variant);
      this.updateImage(card, variant);

      this.setStatus(
        card,
        variant.available === false
          ? 'This size is sold out.'
          : ''
      );
    } else {
      card.sizeToggle.textContent =
        'Select Size';

      card.sizeHeading.textContent =
        'Choose your size';

      card.addButton.textContent =
        'Select a Size';

      card.addButton.disabled = true;

      this.setStatus(
        card,
        selectionComplete
          ? 'This size combination is unavailable.'
          : ''
      );
    }
  }

  updateSelectedButtons(card) {
    card.element
      .querySelectorAll(
        '.js-article-cta-option'
      )
      .forEach((button) => {
        const index = Number(
          button.dataset.optionIndex
        );

        const selected =
          card.selectedOptions[index] ===
          button.dataset.optionValue;

        button.classList.toggle(
          'is-selected',
          selected
        );

        button.setAttribute(
          'aria-pressed',
          selected ? 'true' : 'false'
        );
      });
  }

  updateOptionAvailability(card) {
    card.element
      .querySelectorAll(
        '.js-article-cta-option'
      )
      .forEach((button) => {
        const index = Number(
          button.dataset.optionIndex
        );

        const value =
          button.dataset.optionValue;

        button.disabled =
          !this.hasAvailableVariant(
            card,
            index,
            value
          );

        button.classList.toggle(
          'is-unavailable',
          button.disabled
        );
      });
  }

  hasAvailableVariant(
    card,
    changedIndex,
    changedValue
  ) {
    return card.product.variants.some(
      (variant) => {
        if (variant.available === false) {
          return false;
        }

        if (
          this.getOption(
            variant,
            changedIndex
          ) !== changedValue
        ) {
          return false;
        }

        return Object.entries(
          card.selectedOptions
        ).every(([index, value]) => {
          const numericIndex =
            Number(index);

          if (
            numericIndex === changedIndex
          ) {
            return true;
          }

          return (
            this.getOption(
              variant,
              numericIndex
            ) === value
          );
        });
      }
    );
  }

  isSelectionComplete(card) {
    if (
      card.bandIndex >= 0 &&
      card.cupIndex >= 0
    ) {
      return Boolean(
        card.selectedOptions[
          card.bandIndex
        ] &&
          card.selectedOptions[
            card.cupIndex
          ]
      );
    }

    return Boolean(
      card.selectedOptions[
        card.sizeIndex
      ]
    );
  }

  findSelectedVariant(card) {
    return (
      card.product.variants.find(
        (variant) =>
          Object.entries(
            card.selectedOptions
          ).every(([index, value]) =>
            this.getOption(
              variant,
              Number(index)
            ) === value
          )
      ) || null
    );
  }

  selectedSizeLabel(card) {
    if (
      card.bandIndex >= 0 &&
      card.cupIndex >= 0
    ) {
      return (
        card.selectedOptions[
          card.bandIndex
        ] +
        card.selectedOptions[
          card.cupIndex
        ]
      );
    }

    return (
      card.selectedOptions[
        card.sizeIndex
      ] || ''
    );
  }

  getOption(variant, index) {
    if (!variant || index < 0) {
      return '';
    }

    if (Array.isArray(variant.options)) {
      return String(
        variant.options[index] || ''
      ).trim();
    }

    return String(
      variant[`option${index + 1}`] || ''
    ).trim();
  }

  updatePrice(card, variant) {
    if (card.price) {
      card.price.textContent =
        this.money(variant.price);
    }

    if (!card.comparePrice) {
      return;
    }

    if (
      Number(variant.compare_at_price) >
      Number(variant.price)
    ) {
      card.comparePrice.textContent =
        this.money(
          variant.compare_at_price
        );

      card.comparePrice.hidden = false;
    } else {
      card.comparePrice.textContent = '';
      card.comparePrice.hidden = true;
    }
  }

  updateImage(card, variant) {
    if (
      !card.image ||
      !variant.featured_image
    ) {
      return;
    }

    const source =
      typeof variant.featured_image === 'string'
        ? variant.featured_image
        : variant.featured_image.src ||
          variant.featured_image.url;

    if (!source) {
      return;
    }

    card.image.src = source;
    card.image.srcset = '';
  }

  money(cents) {
    const currency =
      window.Shopify?.currency?.active ||
      'USD';

    return new Intl.NumberFormat(
      undefined,
      {
        style: 'currency',
        currency,
      }
    ).format(
      Number(cents || 0) / 100
    );
  }

  formatColorName(name) {
    return String(name)
      .replace(/[-_]+/g, ' ')
      .replace(/\b\w/g, (letter) =>
        letter.toUpperCase()
      );
  }

  swatchColor(name) {
    const value = String(name)
      .toLowerCase();

    const colors = {
      black: '#171717',
      white: '#ffffff',
      taupe: '#c8b5a3',
      mocha: '#92715f',
      sienna: '#a86f55',
      espresso: '#4a332b',
      apricot: '#e7b8a0',
      haze: '#a8a8ad',
      shadow: '#69676d',
      pinot: '#743c4a',
      forest: '#344f43',
      beige: '#d6c2ae',
      nude: '#d8bba4',
      almond: '#d7bca6',
      sand: '#d1b69d',
      ivory: '#f3efe7',
      cream: '#eee8dd',
      pink: '#e7bfc0',
      blush: '#e6c3be',
      rose: '#c98f94',
      red: '#a8373d',
      burgundy: '#6e2939',
      purple: '#88738f',
      lavender: '#b5a4c4',
      blue: '#7893aa',
      navy: '#25354c',
      green: '#87958a',
      sage: '#aeb8aa',
      gray: '#a8a8a8',
      grey: '#a8a8a8',
    };

    const key = Object.keys(colors).find(
      (color) => value.includes(color)
    );

    return key
      ? colors[key]
      : '#d7d2ce';
  }

  async addToBag(card) {
    const variant =
      card.selectedVariant;

    if (
      !variant ||
      variant.available === false
    ) {
      return;
    }

    const button = card.addButton;

    button.disabled = true;
    button.classList.add('is-loading');
    button.textContent = 'Adding...';

    try {
      const properties = {};

      /*
       * Add the same product-tag information used by the
       * existing ThirdLove product forms.
       */
      if (
        typeof window.filterProductTags === 'function' &&
        Array.isArray(card.product.tags)
      ) {
        properties._tags =
          window.filterProductTags(
            card.product.tags
          );
      }

      const payload = {
        id: Number(variant.id),
        quantity: 1,
        properties,
      };

      let result;

      /*
       * Use ThirdLove's existing cart manager. This refreshes its
       * local cart data before the drawer is rendered.
       */
      if (
        window.cartManager &&
        typeof window.cartManager.addItem === 'function'
      ) {
        result =
          await window.cartManager.addItem(
            payload
          );

        /*
         * component-cart-body listens for update-cart. It rebuilds
         * the drawer, updates the bag badges and opens the drawer.
         */
        window.dispatchEvent(
          new CustomEvent('update-cart', {
            detail: {
              openCartDrawer: true,
            },
          })
        );
      } else {
        /*
         * Safe fallback if the theme cart manager is unavailable.
         */
        const response = await fetch(
          `${this.rootPath}cart/add.js`,
          {
            method: 'POST',
            headers: {
              'Content-Type':
                'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({
              items: [payload],
            }),
          }
        );

        result = await response.json();

        if (!response.ok || result.status) {
          throw new Error(
            result.description ||
              result.message ||
              'Unable to add this item.'
          );
        }

        window.dispatchEvent(
          new CustomEvent('update-cart', {
            detail: {
              openCartDrawer: true,
            },
          })
        );

        window.dispatchEvent(
          new CustomEvent(
            'open-cart-drawer'
          )
        );
      }

      button.textContent =
        'Added to Bag';

      this.setStatus(
        card,
        'Added to your bag.'
      );
    } catch (error) {
      console.error(
        'Article CTA Add to Bag:',
        error
      );

      button.textContent =
        'Add to Bag';

      button.disabled = false;

      this.setStatus(
        card,
        error.message ||
          'Unable to add this item.',
        true
      );
    } finally {
      button.classList.remove(
        'is-loading'
      );
    }
  }

  setStatus(
    card,
    message,
    isError = false
  ) {
    if (!card.status) {
      return;
    }

    card.status.textContent = message;

    card.status.classList.toggle(
      'is-error',
      isError
    );
  }
}

function initializeArticleProductGridCTAs(
  scope = document
) {
  scope
    .querySelectorAll(
      '.js-article-product-grid-cta:not([data-initialized="true"])'
    )
    .forEach((root) => {
      root.dataset.initialized = 'true';

      const controller =
        new ArticleProductGridCTAController(
          root
        );

      root.articleProductGridCTAController =
        controller;

      controller
        .initialize()
        .catch((error) => {
          console.error(
            'Article product grid CTA initialization:',
            error
          );
        });
    });
}

if (document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    () => {
      initializeArticleProductGridCTAs();
    }
  );
} else {
  initializeArticleProductGridCTAs();
}

document.addEventListener(
  'shopify:section:load',
  (event) => {
    initializeArticleProductGridCTAs(
      event.target
    );
  }
);